import { MapContext } from 'components/maps/MapContext';
import { FlyToInterpolator } from 'deck.gl';
import { MjolnirGestureEvent } from 'mjolnir.js';
import { Dispatch, SetStateAction, useCallback, useContext } from 'react';
import { ViewState } from 'react-map-gl/dist/esm/types';
import { getScaledDuration } from '../../utils';
import { PickedObject } from '../types';

type UseMapClusterEventsOptions = {
  setViewState: Dispatch<SetStateAction<Partial<ViewState>>>;
};

export const useMapClusterEvents = ({ setViewState }: UseMapClusterEventsOptions) => {
  const { setEntities } = useContext(MapContext);

  const onRightClick = useCallback(
    ({ object }: PickedObject<{ clusters: { ids: string }[] }>, event: MjolnirGestureEvent) => {
      if (!object?.clusters) {
        return;
      }
      event.preventDefault();
      const selectedIds: string[] = Object.values(object.clusters)
        .map(({ ids }) => ids)
        .flat();
      setEntities(selectedIds);
    },
    [setEntities]
  );

  const onLeftClick = useCallback(
    ({ object }, _event: MjolnirGestureEvent) => {
      if (!object?.geometry) {
        return;
      }
      // zoom to break up cluster
      setViewState((prev) => ({
        ...prev,
        latitude: object.geometry.coordinates[1],
        longitude: object.geometry.coordinates[0],
        zoom: object.onClickZoomLevel,
        transitionDuration: getScaledDuration(
          prev.latitude!,
          prev.longitude!,
          object.geometry.coordinates[1],
          object.geometry.coordinates[0],
          prev.zoom!,
          object.onClickZoomLevel
        ),
        transitionInterpolator: new FlyToInterpolator(),
      }));
    },
    [setViewState]
  );

  const handleOnClick = useCallback(
    (pickingInfo, event: MjolnirGestureEvent) => {
      if (event.rightButton) {
        return onRightClick(pickingInfo, event);
      }
      onLeftClick(pickingInfo, event);
    },
    [onLeftClick, onRightClick]
  );

  return { handleOnClick, onRightClick, onLeftClick };
};
